import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useState } from 'react';
import { ArrowLeft, Check, Copy, Infinity as InfinityIcon, LockKeyhole, RefreshCw, X } from 'lucide-react';
import { createTeamInvitationLink, getLatestTeamInvitationLink, teamApiError } from './teamApi';

const quotaOptions = [
  { value: 0, label: '无额度限制', icon: InfinityIcon },
  { value: 1, label: '周期额度', icon: RefreshCw },
  { value: 2, label: '固定额度', icon: LockKeyhole },
];

export default function TeamInvitationDialog({ team, onClose, showNotice }) {
  const [step, setStep] = useState('settings');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [maxUses, setMaxUses] = useState(5);
  const [quotaType, setQuotaType] = useState(0);
  const [quotaCycle, setQuotaCycle] = useState(3);
  const [quotaAmount, setQuotaAmount] = useState('20000');
  const [linkData, setLinkData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const onKeyDown = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    getLatestTeamInvitationLink(team.id)
      .then(response => {
        if (!active || !response) return;
        setLinkData(response);
        setQuotaType(Number(response.quota_type || 0));
        setQuotaCycle(Number(response.quota_cycle || 3));
        setQuotaAmount(response.quota_amount || '20000');
        setMaxUses(Number(response.max_uses || 5));
        setStep('share');
      })
      .catch(requestError => { if (active) setError(getErrorMessage(teamApiError(requestError, '邀请链接加载失败'))); })
      .finally(() => { if (active) setInitialLoading(false); });
    return () => { active = false; };
  }, [team.id]);

  const invitationUrl = linkData ? `${window.location.origin}/team/invite/${linkData.id}` : '';
  const generate = async event => {
    event.preventDefault();
    const amount = Number(quotaAmount);
    if (quotaType !== 0 && (!Number.isFinite(amount) || amount <= 0)) {
      setError('成员额度必须大于0');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await createTeamInvitationLink({
        id: team.id,
        expires_in_days: Number(expiresInDays),
        max_uses: Number(maxUses),
        quota_type: quotaType,
        quota_cycle: quotaType === 1 ? quotaCycle : null,
        quota_amount: quotaType === 0 ? null : amount,
      });
      setLinkData(response);
      setCopied(false);
      setStep('share');
      showNotice('新的邀请链接已生成');
    } catch (requestError) {
      setError(getErrorMessage(teamApiError(requestError, '邀请链接生成失败')));
    } finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopied(true);
      showNotice('邀请链接已复制，可以直接发给成员');
    } catch { showNotice('复制失败，请手动选择邀请链接', 'error'); }
  };

  return <div className="team-dialog-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="team-dialog" role="dialog" aria-modal="true" aria-labelledby="team-invite-title">
      <header><div>{step === 'share' && <button type="button" aria-label="返回链接设置" onClick={() => setStep('settings')}><ArrowLeft size={15} /></button>}<h2 id="team-invite-title">{step === 'settings' ? '链接设置' : '邀请成员'}</h2></div><button type="button" aria-label="关闭邀请弹窗" onClick={onClose}><X size={15} /></button></header>
      {initialLoading ? <div className="team-dialog__loading"><RefreshCw size={16} />正在读取最新邀请链接…</div> : step === 'settings' ? <form className="team-invite-settings" onSubmit={generate}>
        <p>为“{team.name}”设置邀请链接的有效期、使用次数和新成员默认额度。</p>
        <div className="team-invite-settings__grid"><label><span>链接有效期</span><select value={expiresInDays} onChange={event => setExpiresInDays(event.target.value)}><option value="1">1 天</option><option value="7">7 天</option><option value="30">30 天</option></select></label><label><span>链接可用次数</span><select value={maxUses} onChange={event => setMaxUses(event.target.value)}><option value="1">1 次</option><option value="5">5 次</option><option value="20">20 次</option><option value="100">100 次</option></select></label></div>
        <fieldset><legend>新成员 Tapies 设置</legend><div className="team-quota-options">{quotaOptions.map(option => { const Icon = option.icon; return <button className={quotaType === option.value ? 'is-selected' : ''} type="button" aria-pressed={quotaType === option.value} key={option.value} onClick={() => setQuotaType(option.value)}><Icon size={18} /><span>{option.label}</span></button>; })}</div></fieldset>
        {quotaType === 1 && <div className="team-invite-settings__grid"><label><span>恢复周期</span><select value={quotaCycle} onChange={event => setQuotaCycle(Number(event.target.value))}><option value="1">每日</option><option value="2">每周</option><option value="3">每月</option></select></label><label><span>周期内 Tapies 额度</span><input type="number" min="0.01" step="0.01" value={quotaAmount} onChange={event => setQuotaAmount(event.target.value)} /></label></div>}
        {quotaType === 2 && <label><span>固定 Tapies 额度</span><input type="number" min="0.01" step="0.01" value={quotaAmount} onChange={event => setQuotaAmount(event.target.value)} /></label>}
        {error && <p className="team-dialog__error" role="alert">{error}</p>}
        <footer><button className="is-primary" type="submit" disabled={busy}>{busy ? '生成中…' : '生成新链接'}</button></footer>
      </form> : <div className="team-invite-share">
        <p>分享此链接邀请朋友加入您的团队，他们登录后即可接受邀请。</p>
        <label><span>邀请链接</span><div><input readOnly value={invitationUrl} /><button className={copied ? 'is-copied' : 'is-primary'} type="button" onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? '已复制' : '复制'}</button></div></label>
        <div className="team-invite-share__summary"><i aria-hidden="true" /><div><strong>{linkData?.expires_at ? `有效至 ${new Date(linkData.expires_at).toLocaleString('zh-CN', { hour12: false })}` : `此链接 ${expiresInDays} 天内有效`}</strong><span>剩余可用 {linkData?.remaining_uses ?? maxUses} 次，新成员默认：{quotaOptions.find(option => option.value === quotaType)?.label}</span></div></div>
        <footer><button type="button" onClick={() => setStep('settings')}>编辑链接设置</button></footer>
      </div>}
    </section>
  </div>;
}
